import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import type { Order } from "@/lib/projects";
import {
  getAuftragSharepointWebUrl,
  projectExecutionClassBadgeClass,
  projectWorkflowStatusBadgeClass,
} from "@/lib/projects";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import { isElectronWithOpenFolder } from "@/lib/electron";
import { BestellnummerQuelleBadge } from "@/components/BestellnummerField";
import { toast } from "@/lib/toast";
import {
  PROJECT_ACTION_COL_WIDTH,
  PROJECT_SORTABLE_COLUMNS,
  clampColumnWidth,
  getColumnDefaultWidth,
  getColumnLabel,
  isProjectActionColumn,
  renderCellValue,
} from "@/pages/projekte/columns";
import type {
  HeroCustomField,
  TimesheetBadge,
} from "@/pages/projekte/columns";
import {
  EhDataTableShell,
  HeroHeaderLogo,
  SortableHeader,
  type SortState,
} from "@/components/dataTable";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingProjectTableHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";
import { HoursUsageCell } from "@/pages/projekte/HoursUsageCell";
import { BookedHoursCell } from "@/pages/projekte/BookedHoursCell";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import type { HoursUsageEntry } from "@/pages/projekte/hoursUsage";
import type { BookedHoursEntry } from "@/pages/projekte/useBookedHours";
import {
  auftragsartAppBadgeClass,
  type AuftragsartAppBadgeKind,
} from "@/pages/projekte/auftragsartApp";
import { HeroCreateDialog } from "@/pages/projekte/HeroCreateDialog";
import { HighlightedText } from "@/components/ui/HighlightedText";

const HERO_PROJECT_BASE = "https://login.hero-software.de/partner/Projects/view/";
const HERO_LOGO_LIGHT = "/static/img/hero-logo.png";
const HERO_LOGO_DARK = "/static/img/hero-logo-dark.png";
const HERO_LEGACY_LOGO_LIGHT = "/static/img/plancraft-logo.png";
const HERO_LEGACY_LOGO_DARK = "/static/img/plancraft-logo-dark.png";

export interface ProjectTableProps {
  rows: Order[];
  columns: string[];
  heroFields: HeroCustomField[];
  /** Aktuelle Spaltenbreiten (Pixel) je Spaltenschlüssel. */
  widths: Record<string, number>;
  hoursUsageById?: Record<number, HoursUsageEntry>;
  bookedHoursById?: Record<number, BookedHoursEntry>;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  /** Klick auf eine Zeile öffnet die klassische Detailansicht. */
  onRowOpen: (row: Order) => void;
  /** Wird beim Loslassen des Drag-Handles mit der finalen Breite aufgerufen. */
  onColumnWidthChange?: (key: string, width: number) => void;
  /** Nach erfolgreicher Hero-Anlage: Zeile mit neuer hero_project_id aktualisieren. */
  onHeroCreated?: (orderId: number, heroProjectId: number) => void;
  emptyText?: string;
  sort?: SortState | null;
  onSort?: (key: string) => void;
  /** Aktiver Suchbegriff: hebt Treffer in den Textzellen hervor. */
  highlightQuery?: string;
}

export function ProjectTable({
  rows,
  columns,
  heroFields,
  widths,
  hoursUsageById,
  bookedHoursById,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onRowOpen,
  onColumnWidthChange,
  onHeroCreated,
  emptyText = "Keine Projekte gefunden.",
  sort,
  onSort,
  highlightQuery,
}: ProjectTableProps) {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [heroCreateOrder, setHeroCreateOrder] = useState<Order | null>(null);

  // Infinite Scroll via IntersectionObserver
  useEffect(() => {
    if (!hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting && !loading && !loadingMore) {
          onLoadMore();
        }
      },
      { rootMargin: "240px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore]);

  return (
    <div>
      <div className="hidden md:block">
        <DesktopTable
          rows={rows}
          columns={columns}
          heroFields={heroFields}
          widths={widths}
          hoursUsageById={hoursUsageById}
          bookedHoursById={bookedHoursById}
          onRowOpen={onRowOpen}
          onHeroCreate={setHeroCreateOrder}
          onColumnWidthChange={onColumnWidthChange}
          loading={loading}
          emptyText={emptyText}
          sort={sort}
          onSort={onSort}
          highlightQuery={highlightQuery}
        />
      </div>
      <div className="md:hidden">
        <MobileCards
          rows={rows}
          columns={columns}
          heroFields={heroFields}
          hoursUsageById={hoursUsageById}
          bookedHoursById={bookedHoursById}
          onRowOpen={onRowOpen}
          loading={loading}
          emptyText={emptyText}
          highlightQuery={highlightQuery}
        />
      </div>

      {hasMore && (
        <div ref={sentinelRef} className="flex justify-center py-4 text-sm text-label-tertiary">
          {loadingMore ? "Lade weitere …" : "Weitere Projekte laden …"}
        </div>
      )}
      {!hasMore && rows.length > 0 && (
        <div className="py-4 text-center text-xs text-label-tertiary">
          Ende der Liste · {rows.length} Projekte
        </div>
      )}

      <HeroCreateDialog
        open={heroCreateOrder != null}
        order={heroCreateOrder}
        onClose={() => setHeroCreateOrder(null)}
        onCreated={onHeroCreated}
      />
    </div>
  );
}

/* ─────────────────────────── Desktop ─────────────────────────── */

interface DesktopTableProps {
  rows: Order[];
  columns: string[];
  heroFields: HeroCustomField[];
  widths: Record<string, number>;
  hoursUsageById?: Record<number, HoursUsageEntry>;
  bookedHoursById?: Record<number, BookedHoursEntry>;
  onRowOpen: (row: Order) => void;
  onHeroCreate: (row: Order) => void;
  onColumnWidthChange?: (key: string, width: number) => void;
  loading: boolean;
  emptyText: string;
  sort?: SortState | null;
  onSort?: (key: string) => void;
  highlightQuery?: string;
}

/** Spalten, in denen Suchtreffer hervorgehoben werden (reine Textspalten). */
const HIGHLIGHT_COLUMNS = new Set([
  "auftragsnummer",
  "kunde",
  "ansprechpartner",
  "ort",
  "ort_zusatz",
  "beschreibung",
  "bestellnummer",
  "kundenreferenz",
  "leitweg_id",
]);

interface ProjectDesktopTheadProps {
  dataColumns: string[];
  actionColumns: string[];
  actionRightOffsets: Record<string, number>;
  heroFields: HeroCustomField[];
  activeResize: string | null;
  onResizePointerDown: (e: React.PointerEvent, colKey: string, idx: number) => void;
  /** Floating-Kopie kann dieselben Griffe nutzen (mit synchronisierten `<col>`-Refs). */
  omitResizers: boolean;
  sort?: SortState | null;
  onSort?: (key: string) => void;
}

function ProjectDesktopThead({
  dataColumns,
  actionColumns,
  actionRightOffsets,
  heroFields,
  activeResize,
  onResizePointerDown,
  omitResizers,
  sort,
  onSort,
}: ProjectDesktopTheadProps) {
  return (
    <thead className="eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
      <tr>
        {dataColumns.map((col, idx) => (
          <th
            key={col}
            scope="col"
            className="eh-data-th whitespace-normal break-words px-3 py-2 text-left font-semibold leading-snug"
          >
            {PROJECT_SORTABLE_COLUMNS.has(col) && onSort ? (
              <SortableHeader
                label={getColumnLabel(col, heroFields)}
                columnKey={col}
                sort={sort ?? null}
                onSort={onSort}
              />
            ) : (
              <span className="block" title={getColumnLabel(col, heroFields)}>
                {getColumnLabel(col, heroFields)}
              </span>
            )}
            {!omitResizers && (
              <span
                className={clsx(
                  "eh-data-resizer",
                  activeResize === col && "is-active",
                )}
                role="separator"
                aria-orientation="vertical"
                title="Spaltenbreite ziehen"
                onPointerDown={(e) => onResizePointerDown(e, col, idx)}
              />
            )}
          </th>
        ))}
        {actionColumns.map((col) => (
          <th
            key={col}
            scope="col"
            className="eh-data-action-cell whitespace-nowrap py-2 text-center text-[10px] font-semibold uppercase tracking-wide"
            style={{ right: `${actionRightOffsets[col]}px` }}
            title={getColumnLabel(col, heroFields)}
          >
            <span className="sr-only">{getColumnLabel(col, heroFields)}</span>
            {actionHeaderIcon(col)}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function DesktopTable({
  rows,
  columns,
  heroFields,
  widths,
  hoursUsageById,
  bookedHoursById,
  onRowOpen,
  onHeroCreate,
  onColumnWidthChange,
  loading,
  emptyText,
  sort,
  onSort,
  highlightQuery,
}: DesktopTableProps) {
  // Aktionsspalten und Daten-Spalten getrennt halten – die Aktionsspalten
  // werden rechts „angepinnt" und sollen NICHT resizebar sein.
  const dataColumns = useMemo(
    () => columns.filter((c) => !isProjectActionColumn(c)),
    [columns],
  );
  const actionColumns = useMemo(
    () => columns.filter((c) => isProjectActionColumn(c)),
    [columns],
  );

  // Effektive Spaltenbreiten – verwende gespeicherte Werte, sonst den Default.
  const dataWidths = useMemo(() => {
    return dataColumns.map((col) => {
      const stored = Number(widths?.[col]);
      const defaultWidth = getColumnDefaultWidth(col);
      return Number.isFinite(stored) && stored > 0 ? clampColumnWidth(stored) : defaultWidth;
    });
  }, [dataColumns, widths]);

  // Lokaler State während des aktiven Resize-Vorgangs (für visuelles Feedback,
  // ohne pro Mausbewegung React state zu ändern). Wir mutieren stattdessen
  // direkt die <col> Elemente per Ref.
  const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const floatingColRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const [activeResize, setActiveResize] = useState<string | null>(null);

  // Right-Offsets für die sticky Aktionsspalten berechnen, in derselben
  // Reihenfolge wie sie im DOM auftauchen (links → rechts).
  const actionRightOffsets = useMemo(() => {
    const offsets: Record<string, number> = {};
    // Die letzte Aktionsspalte sitzt rechts (right: 0), dann jeweils +44px.
    let right = 0;
    for (let i = actionColumns.length - 1; i >= 0; i--) {
      const key = actionColumns[i];
      if (!key) continue;
      offsets[key] = right;
      right += PROJECT_ACTION_COL_WIDTH;
    }
    return offsets;
  }, [actionColumns]);

  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const floatingSyncKey = useMemo(
    () =>
      [
        dataColumns.join("|"),
        actionColumns.join("|"),
        dataWidths.join(","),
        rows.length,
      ].join("§"),
    [dataColumns, actionColumns, dataWidths, rows.length],
  );

  const floating = useFloatingProjectTableHeader(tableWrapRef, tableRef, {
    enabled: dataColumns.length > 0,
    syncKey: floatingSyncKey,
  });

  function startResize(event: React.PointerEvent, colKey: string, idx: number) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    setActiveResize(colKey);

    const startX = event.clientX;
    const startWidth = dataWidths[idx] ?? getColumnDefaultWidth(colKey);
    const colEl = colRefs.current[colKey];
    let lastWidth = startWidth;

    function applyWidth(width: number) {
      lastWidth = clampColumnWidth(width);
      if (colEl) {
        colEl.style.width = `${lastWidth}px`;
      }
      const floatCol = floatingColRefs.current[colKey];
      if (floatCol) {
        floatCol.style.width = `${lastWidth}px`;
      }
    }

    function onMove(e: PointerEvent) {
      const delta = e.clientX - startX;
      applyWidth(startWidth + delta);
    }

    function onUp() {
      target.releasePointerCapture?.(event.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setActiveResize(null);
      if (lastWidth !== startWidth) {
        onColumnWidthChange?.(colKey, lastWidth);
      }
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  return (
    <>
      <EhDataTableShell ref={tableWrapRef}>
        <table ref={tableRef} className="eh-data-table divide-y divide-separator text-sm">
          <colgroup>
            {dataColumns.map((col, idx) => (
              <col
                key={col}
                ref={(el) => {
                  colRefs.current[col] = el;
                }}
                style={{ width: `${dataWidths[idx]}px` }}
              />
            ))}
            {actionColumns.map((col) => (
              <col key={col} style={{ width: `${PROJECT_ACTION_COL_WIDTH}px` }} />
            ))}
          </colgroup>

          <ProjectDesktopThead
            dataColumns={dataColumns}
            actionColumns={actionColumns}
            actionRightOffsets={actionRightOffsets}
            heroFields={heroFields}
            activeResize={activeResize}
            onResizePointerDown={startResize}
            omitResizers={false}
            sort={sort}
            onSort={onSort}
          />

          <tbody className="divide-y divide-separator bg-card">
            {rows.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-sm text-label-tertiary"
                >
                  {emptyText}
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr
                key={row.id}
                onClick={() => onRowOpen(row)}
                className="eh-data-row cursor-pointer align-middle transition"
              >
                {dataColumns.map((col) => {
                  const cell = renderCellValue(row, col, heroFields);
                  if (col === "timesheets" && cell.badges) {
                    return (
                      <td
                        key={col}
                        className="eh-data-body-cell min-w-0 align-middle whitespace-normal px-3 py-2 text-label"
                      >
                        <TimesheetBadges badges={cell.badges} />
                      </td>
                    );
                  }
                  if (col === "hours_usage") {
                    return (
                      <td
                        key={col}
                        className="eh-data-body-cell min-w-0 align-middle whitespace-nowrap px-3 py-2 text-label"
                      >
                        <HoursUsageCell entry={hoursUsageById?.[row.id]} />
                      </td>
                    );
                  }
                  if (col === "booked_hours") {
                    return (
                      <td
                        key={col}
                        className="eh-data-body-cell min-w-0 align-middle whitespace-nowrap px-3 py-2 text-label"
                      >
                        <BookedHoursCell entry={bookedHoursById?.[row.id]} />
                      </td>
                    );
                  }
                  return (
                    <td
                      key={col}
                      className={clsx(
                        "eh-data-body-cell min-w-0 align-middle whitespace-normal break-words px-3 py-2 text-label",
                        cell.mono && "font-mono",
                        col === "status" && "text-label-secondary",
                      )}
                      title={
                        col === "status" && cell.secondaryStatusLabel
                          ? `${cell.value || ""} / ${cell.secondaryStatusLabel}`
                          : cell.value || undefined
                      }
                    >
                      {col === "status" ? (
                        <StatusBadge
                          value={cell.value}
                          statusKey={cell.statusKey}
                          secondaryValue={cell.secondaryStatusLabel}
                          secondaryStatusKey={cell.secondaryStatusKey}
                        />
                      ) : cell.executionClassBadge && cell.value ? (
                        <span className={projectExecutionClassBadgeClass(cell.value)}>
                          {cell.value}
                        </span>
                      ) : cell.auftragsartBadge ? (
                        <AuftragsartAppBadge
                          value={cell.value}
                          kind={cell.auftragsartBadge}
                        />
                      ) : (
                        <span className="block w-full break-words">
                          {cell.value ? (
                            highlightQuery && HIGHLIGHT_COLUMNS.has(col) ? (
                              <HighlightedText text={cell.value} query={highlightQuery} />
                            ) : (
                              cell.value
                            )
                          ) : (
                            "—"
                          )}
                          {col === "bestellnummer" && cell.bestellnummerQuelle ? (
                            <BestellnummerQuelleBadge
                              quelle={cell.bestellnummerQuelle}
                              className="ml-1.5 align-middle"
                            />
                          ) : null}
                          {col === "auftragsnummer" && (
                            <MatchReasonBadges reasons={row.match_reasons} />
                          )}
                        </span>
                      )}
                    </td>
                  );
                })}
                {actionColumns.map((col) => (
                  <td
                    key={col}
                    className="eh-data-action-cell align-middle py-2 text-center leading-none"
                    style={{ right: `${actionRightOffsets[col]}px` }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ActionButton
                      column={col}
                      row={row}
                      onRowOpen={onRowOpen}
                      onHeroCreate={onHeroCreate}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </EhDataTableShell>

      {floating &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
            style={{
              top: floating.top,
              left: floating.left,
              width: floating.width,
            }}
            aria-hidden
          >
            <div
              className="pointer-events-auto eh-floating-table-header-sync"
              style={{
                width: floating.tableWidth,
                transform: `translateX(${-floating.scrollLeft}px)`,
                ["--eh-table-scroll-left" as string]: `${floating.scrollLeft}px`,
              }}
            >
              <table className="eh-data-table w-full text-sm">
                <colgroup>
                  {dataColumns.map((col, idx) => (
                    <col
                      key={col}
                      ref={(el) => {
                        floatingColRefs.current[col] = el;
                      }}
                      style={{ width: `${dataWidths[idx]}px` }}
                    />
                  ))}
                  {actionColumns.map((col) => (
                    <col key={col} style={{ width: `${PROJECT_ACTION_COL_WIDTH}px` }} />
                  ))}
                </colgroup>
                <ProjectDesktopThead
                  dataColumns={dataColumns}
                  actionColumns={actionColumns}
                  actionRightOffsets={actionRightOffsets}
                  heroFields={heroFields}
                  activeResize={activeResize}
                  onResizePointerDown={startResize}
                  omitResizers={false}
                  sort={sort}
                  onSort={onSort}
                />
              </table>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/* ───────────────────────── Aktionsspalten ───────────────────────── */

/**
 * Wie `bindOrderRowInteractions` in auftraege.js: zuerst lokalen Pfad via
 * `envAPI.openFolder`, bei Fehler oder fehlendem Pfad SharePoint-Web-URL.
 */
async function handleOpenProjectFolder(e: React.MouseEvent, row: Order): Promise<void> {
  e.stopPropagation();
  const path = (row.ordnerpfad || row.sharepoint_path || "").trim();
  if (!path) {
    const webUrl = await getAuftragSharepointWebUrl(row.id);
    if (webUrl) window.open(webUrl, "_blank", "noopener");
    return;
  }
  const openFolder = window.envAPI?.openFolder;
  if (typeof openFolder !== "function") return;
  const err = await openFolder(path);
  if (err) {
    const webUrl = await getAuftragSharepointWebUrl(row.id);
    if (webUrl) {
      window.open(webUrl, "_blank", "noopener");
      return;
    }
    toast.error(String(err));
  }
}

function actionHeaderIcon(col: string): React.ReactNode {
  switch (col) {
    case "open":
      return (
        <span
          className="inline-flex justify-center text-[1.25rem] leading-none"
          aria-hidden
        >
          📂
        </span>
      );
    case "hero":
      return <HeroHeaderLogo className="mx-auto h-7 w-7 object-contain" />;
    case "cover_print_default":
      return <span aria-hidden="true">🖨️</span>;
    default:
      return null;
  }
}

function ActionButton({
  column,
  row,
  onHeroCreate,
}: {
  column: string;
  row: Order;
  onRowOpen: (row: Order) => void;
  onHeroCreate?: (row: Order) => void;
}) {
  const isDark = useIsDarkTheme();
  const { user } = useCurrentUser();
  const { open: openProjectOverview } = useProjectOverview();
  const showFolderMissingAction = userIsBuero(user) && isElectronWithOpenFolder();

  if (column === "hero") {
    // Angebot in der App geschrieben → Hero-Button ausblenden.
    if (row.has_app_angebot === true) return null;
    const pid = row.hero_project_id;
    const legacy = row.legacy_import === true;
    const heroSrc = legacy
      ? isDark
        ? HERO_LEGACY_LOGO_DARK
        : HERO_LEGACY_LOGO_LIGHT
      : isDark
        ? HERO_LOGO_DARK
        : HERO_LOGO_LIGHT;
    if (pid) {
      const url = `${HERO_PROJECT_BASE}${encodeURIComponent(String(pid))}`;
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener"
          title="Hero-Projekt öffnen"
          className="eh-data-action-btn"
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={heroSrc}
            alt="Hero"
            style={{ width: 22, height: 22, objectFit: "contain" }}
          />
        </a>
      );
    }
    return (
      <button
        type="button"
        title="Kein Hero-Projekt – in Hero anlegen"
        className="eh-data-action-btn"
        style={{ opacity: 0.5 }}
        onClick={(e) => {
          e.stopPropagation();
          onHeroCreate?.(row);
        }}
      >
        <img
          src={isDark ? HERO_LEGACY_LOGO_DARK : HERO_LEGACY_LOGO_LIGHT}
          alt="In Hero anlegen"
          style={{ width: 22, height: 22, objectFit: "contain" }}
        />
      </button>
    );
  }

  if (column === "open") {
    const hasFolder = Boolean(
      row.ordnerpfad ||
        row.sharepoint_path ||
        row.sharepoint_folder_id ||
        row.has_folder,
    );
    if (!hasFolder) {
      if (showFolderMissingAction) {
        return (
          <button
            type="button"
            className="eh-data-action-btn"
            title="Ordner suchen oder anlegen"
            onClick={(e) => {
              e.stopPropagation();
              openProjectOverview(row.id);
            }}
          >
            <span className="text-[1.1rem] leading-none" aria-hidden>
              ➕
            </span>
          </button>
        );
      }
      return (
        <span
          className="eh-data-action-btn inline-flex cursor-default items-center justify-center"
          title="Kein Projektordner hinterlegt"
        >
          <span className="text-[1.25rem] leading-none opacity-35 grayscale" aria-hidden>
            📂
          </span>
        </span>
      );
    }
    return (
      <button
        type="button"
        className="eh-data-action-btn"
        title="Ordner öffnen"
        onClick={(e) => {
          void handleOpenProjectFolder(e, row);
        }}
      >
        <span className="text-[1.35rem] leading-none" aria-hidden>
          📂
        </span>
      </button>
    );
  }

  if (column === "cover_print_default") {
    return (
      <button
        type="button"
        className="eh-data-action-btn"
        title="Deckblatt über Standarddrucker drucken"
        onClick={(e) => {
          e.stopPropagation();
          window.location.href = `/auftraege.html?auftrag_id=${row.id}&action=cover_print`;
        }}
      >
        🖨️
      </button>
    );
  }

  return null;
}

/* ─────────────────────────── Mobile ─────────────────────────── */

function MobileCards({
  rows,
  columns,
  heroFields,
  hoursUsageById,
  bookedHoursById,
  onRowOpen,
  loading,
  emptyText,
  highlightQuery,
}: {
  rows: Order[];
  columns: string[];
  heroFields: HeroCustomField[];
  hoursUsageById?: Record<number, HoursUsageEntry>;
  bookedHoursById?: Record<number, BookedHoursEntry>;
  onRowOpen: (row: Order) => void;
  loading: boolean;
  emptyText: string;
  highlightQuery?: string;
}) {
  if (rows.length === 0 && !loading) {
    return (
      <div className="rounded-ios border border-dashed border-separator bg-card p-6 text-center text-sm text-label-tertiary">
        {emptyText}
      </div>
    );
  }
  return (
    <ul className="space-y-2">
      {rows.map((row) => {
        const number = renderCellValue(row, "auftragsnummer", heroFields);
        const customer = renderCellValue(row, "kunde", heroFields);
        const ort = renderCellValue(row, "ort", heroFields);
        const ortZusatz = renderCellValue(row, "ort_zusatz", heroFields);
        const beschreibung = renderCellValue(row, "beschreibung", heroFields);
        const status = renderCellValue(row, "status", heroFields);
        const timesheets = renderCellValue(row, "timesheets", heroFields);
        const restColumns = columns.filter(
          (c) =>
            ![
              "auftragsnummer",
              "kunde",
              "ort",
              "ort_zusatz",
              "beschreibung",
              "status",
              "timesheets",
              ...["open", "hero", "cover_print_default"],
            ].includes(c),
        );
        return (
          <li key={row.id}>
            <button
              type="button"
              onClick={() => onRowOpen(row)}
              className="flex w-full flex-col gap-1.5 rounded-ios border border-separator bg-card px-4 py-3 text-left shadow-ios transition hover:bg-fill-tertiary"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-sm font-semibold text-label">
                  {number.value ? (
                    <HighlightedText text={number.value} query={highlightQuery} />
                  ) : (
                    "—"
                  )}
                </span>
                <StatusBadge
                  value={status.value}
                  statusKey={status.statusKey}
                  secondaryValue={status.secondaryStatusLabel}
                  secondaryStatusKey={status.secondaryStatusKey}
                />
              </div>
              <div className="text-sm font-medium text-label">
                {customer.value ? (
                  <HighlightedText text={customer.value} query={highlightQuery} />
                ) : (
                  "—"
                )}
              </div>
              {(ort.value || ortZusatz.value) && (
                <div className="text-xs text-label-secondary">
                  <HighlightedText
                    text={[ort.value, ortZusatz.value].filter(Boolean).join(" · ")}
                    query={highlightQuery}
                  />
                </div>
              )}
              {beschreibung.value && (
                <div className="line-clamp-2 text-xs text-label-secondary">
                  <HighlightedText text={beschreibung.value} query={highlightQuery} />
                </div>
              )}
              <MatchReasonBadges reasons={row.match_reasons} />
              {timesheets.badges && timesheets.badges.length > 0 && (
                <div className="mt-1">
                  <TimesheetBadges badges={timesheets.badges} />
                </div>
              )}
              {restColumns.length > 0 && (
                <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-label-secondary">
                  {restColumns.map((col) => {
                    if (col === "hours_usage") {
                      return (
                        <div key={col} className="contents">
                          <dt className="text-label-tertiary">{getColumnLabel(col, heroFields)}</dt>
                          <dd className="text-label">
                            <HoursUsageCell entry={hoursUsageById?.[row.id]} />
                          </dd>
                        </div>
                      );
                    }
                    if (col === "booked_hours") {
                      return (
                        <div key={col} className="contents">
                          <dt className="text-label-tertiary">{getColumnLabel(col, heroFields)}</dt>
                          <dd className="text-label">
                            <BookedHoursCell entry={bookedHoursById?.[row.id]} />
                          </dd>
                        </div>
                      );
                    }
                    const cell = renderCellValue(row, col, heroFields);
                    if (!cell.value) return null;
                    return (
                      <div key={col} className="contents">
                        <dt className="text-label-tertiary">{getColumnLabel(col, heroFields)}</dt>
                        <dd className="truncate text-label">
                          {cell.executionClassBadge && cell.value ? (
                            <span className={projectExecutionClassBadgeClass(cell.value)}>
                              {cell.value}
                            </span>
                          ) : cell.auftragsartBadge ? (
                            <AuftragsartAppBadge
                              value={cell.value}
                              kind={cell.auftragsartBadge}
                            />
                          ) : (
                            <>
                              {cell.value}
                              {col === "bestellnummer" && cell.bestellnummerQuelle ? (
                                <>
                                  {" "}
                                  <BestellnummerQuelleBadge quelle={cell.bestellnummerQuelle} />
                                </>
                              ) : null}
                            </>
                          )}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/* ─────────────────────────── Status-Badges ─────────────────────────── */

function StatusBadge({
  value,
  statusKey,
  secondaryValue,
  secondaryStatusKey,
}: {
  value: string | null;
  statusKey?: string;
  secondaryValue?: string | null;
  secondaryStatusKey?: string;
}) {
  if (!value) return <span className="text-label-tertiary">—</span>;
  return (
    <span className="eh-wf-badge-stack">
      <span className={projectWorkflowStatusBadgeClass(statusKey || value)}>
        {value}
      </span>
      {secondaryValue ? (
        <span className={projectWorkflowStatusBadgeClass(secondaryStatusKey || secondaryValue)}>
          {secondaryValue}
        </span>
      ) : null}
    </span>
  );
}

function AuftragsartAppBadge({
  value,
  kind,
}: {
  value: string | null;
  kind: AuftragsartAppBadgeKind;
}) {
  if (!value) return <span className="text-label-tertiary">—</span>;
  return <span className={auftragsartAppBadgeClass(kind)}>{value}</span>;
}

/**
 * Dezente Treffergrund-Badges der hybriden Suche (z. B. „ähnliche
 * Schreibweise", „inhaltlich ähnlich"). Nur bei aktiver Suche gefüllt.
 */
function MatchReasonBadges({ reasons }: { reasons?: string[] | null }) {
  const visible = (reasons || []).filter(Boolean).slice(0, 3);
  if (visible.length === 0) return null;
  return (
    <span className="mt-0.5 flex flex-wrap gap-1">
      {visible.map((reason) => (
        <span
          key={reason}
          className="inline-flex items-center rounded-full bg-fill-tertiary px-1.5 py-px text-[10px] font-medium text-label-secondary"
        >
          {reason}
        </span>
      ))}
    </span>
  );
}

function TimesheetBadges({ badges }: { badges: TimesheetBadge[] }) {
  if (!badges.length) return <span className="text-label-tertiary">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {badges.map((b, idx) => (
        <span key={`${b.label}-${idx}`} className={clsx("eh-ts-badge", `eh-ts-badge--${b.status}`)}>
          {b.label}
        </span>
      ))}
    </div>
  );
}

/* ─────────────────────────── Helper ─────────────────────────── */

function useIsDarkTheme(): boolean {
  const [isDark, setIsDark] = useState<boolean>(() => readIsDark());
  useEffect(() => {
    const root = document.documentElement;
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() => setIsDark(readIsDark()));
    obs.observe(root, { attributes: true, attributeFilter: ["data-theme", "class"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

function readIsDark(): boolean {
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  if (root.getAttribute("data-theme") === "dark") return true;
  if (root.classList.contains("dark")) return true;
  return false;
}
